import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const CookiePreferencesPage: React.FC = () => {
  const [analyticsChecked, setAnalyticsChecked] = useState(false);
  const [marketingChecked, setMarketingChecked] = useState(false);
  const [functionalChecked, setFunctionalChecked] = useState(false);
  
  const { t } = useTranslation();

  // Load saved preferences on component mount
  useEffect(() => {
    const savedPreferences = localStorage.getItem('cookie-preferences');
    if (savedPreferences) {
      const prefs = JSON.parse(savedPreferences);
      setAnalyticsChecked(prefs.analytics || false);
      setMarketingChecked(prefs.marketing || false);
      setFunctionalChecked(prefs.functional || false);
    }
  }, []);

  const handleSavePreferences = () => {
    const preferences = {
      necessary: true, // Always enabled
      analytics: analyticsChecked,
      marketing: marketingChecked,
      functional: functionalChecked
    };

    localStorage.setItem('cookie-preferences', JSON.stringify(preferences));
    localStorage.setItem('cookie-consent', 'custom');

    alert(t('cookiePreferences.savedMessage', 'Cookie preferences saved successfully!'));
  };

  const handleAcceptAll = () => {
    const preferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };

    localStorage.setItem('cookie-preferences', JSON.stringify(preferences));
    localStorage.setItem('cookie-consent', 'accepted');

    alert(t('cookiePreferences.acceptedAllMessage', 'All cookies accepted!'));
  };

  const handleDeclineAll = () => {
    const preferences = {
      necessary: true, // Necessary cookies remain enabled
      analytics: false,
      marketing: false,
      functional: false
    };

    localStorage.setItem('cookie-preferences', JSON.stringify(preferences));
    localStorage.setItem('cookie-consent', 'declined');

    alert(t('cookiePreferences.declinedAllMessage', 'Non-essential cookies declined.'));
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">
        {t('cookiePreferences.title', 'Cookie Preferences')}
      </h1>
      
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <p className="text-gray-700 mb-6">
          {t('cookiePreferences.description', 'When you visit any website, it may store or retrieve information on your browser, mostly in the form of cookies. This information might be about you, your preferences, or your device and is mostly used to make the site work as you expect it to. The information does not usually directly identify you, but it can give you a more personalized web experience.')}
        </p>
        
        <p className="text-gray-700 mb-8">
          {t('cookiePreferences.explanation', 'Because we respect your right to privacy, you can choose not to allow some types of cookies. Below is a list of the different types of cookies and your options regarding their use on our site.')}
        </p>
      </div>

      <div className="space-y-4">
        {/* Necessary Cookies */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="necessary-cookies"
                type="checkbox"
                checked={true}
                onChange={() => {}} // Necessary cookies cannot be disabled
                className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                disabled
              />
            </div>
            <div className="ml-3">
              <label htmlFor="necessary-cookies" className="block text-sm font-medium text-gray-900">
                {t('cookiePreferences.necessary.title', 'Strictly Necessary Cookies')}
              </label>
              <p className="mt-1 text-sm text-gray-600">
                {t('cookiePreferences.necessary.description', 'These cookies are essential for the website to function properly. They enable basic functions like page navigation and access to secure areas of the website. The website cannot function properly without these cookies.')}
              </p>
            </div>
          </div>
        </div>

        {/* Analytics Cookies */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="analytics-cookies"
                type="checkbox"
                checked={analyticsChecked}
                onChange={(e) => setAnalyticsChecked(e.target.checked)}
                className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="analytics-cookies" className="block text-sm font-medium text-gray-900">
                {t('cookiePreferences.analytics.title', 'Analytics Cookies')}
              </label>
              <p className="mt-1 text-sm text-gray-600">
                {t('cookiePreferences.analytics.description', 'These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us know which pages are the most and least popular and see how visitors move around the site.')}
              </p>
            </div>
          </div>
        </div>

        {/* Marketing Cookies */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="marketing-cookies"
                type="checkbox"
                checked={marketingChecked}
                onChange={(e) => setMarketingChecked(e.target.checked)}
                className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="marketing-cookies" className="block text-sm font-medium text-gray-900">
                {t('cookiePreferences.marketing.title', 'Marketing Cookies')}
              </label>
              <p className="mt-1 text-sm text-gray-600">
                {t('cookiePreferences.marketing.description', 'These cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant advertisements.')}
              </p>
            </div>
          </div>
        </div>

        {/* Functional Cookies */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="functional-cookies"
                type="checkbox"
                checked={functionalChecked}
                onChange={(e) => setFunctionalChecked(e.target.checked)}
                className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="functional-cookies" className="block text-sm font-medium text-gray-900">
                {t('cookiePreferences.functional.title', 'Functional Cookies')}
              </label>
              <p className="mt-1 text-sm text-gray-600">
                {t('cookiePreferences.functional.description', 'These cookies enable the website to provide enhanced functionality and personalization. They may be set by us or by third party providers whose services we have added to our pages.')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-4">
        <button
          onClick={handleSavePreferences}
          className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {t('cookiePreferences.savePreferences', 'Save Preferences')}
        </button>
        <button
          onClick={handleAcceptAll}
          className="px-6 py-3 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
        >
          {t('cookiePreferences.acceptAll', 'Accept All Cookies')}
        </button>
        <button
          onClick={handleDeclineAll}
          className="px-6 py-3 bg-red-600 text-white font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          {t('cookiePreferences.declineAll', 'Decline Non-Essential')}
        </button>
      </div>
    </div>
  );
};

export default CookiePreferencesPage;
